import { useState, useEffect, useCallback, useRef } from 'react';
import { Save, Phone, Mail, MapPin, Building, Loader2 } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useAutoSave } from '../../hooks/useAutoSave';
import { adminFetch, getAuthHeaders } from '../../services/adminApi';

import { PageTitle } from './shared';

const BusinessInfoEditor = () => {
  const [businessData, setBusinessData] = useState({
    name: '',
    address: '',
    phone: '',
    direct_line_phone: '',
    direct_line_label: '',
    email: '',
    quickstart_fee: ''
  });
  const [loading, setLoading] = useState(false);
  const businessDataRef = useRef(businessData);

  // Keep ref updated
  useEffect(() => {
    businessDataRef.current = businessData;
  }, [businessData]);

  const performSave = useCallback(async () => {
    const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/business-info`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(businessDataRef.current)
    });

    if (!response.ok) {
      throw new Error('Failed to save');
    }
  }, []);

  const { isDirty, isSaving, scheduleAutosave, saveNow } = useAutoSave(performSave, 5000);

  useEffect(() => {
    fetchBusinessData();
  }, []);

  const fetchBusinessData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/business-info`);
      if (response.ok) {
        const data = await response.json();
        setBusinessData(data);
      }
    } catch (error) {
      console.error('Failed to load business data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setBusinessData(prev => ({ ...prev, [field]: value }));
    scheduleAutosave();
  };

  if (loading) {
    return <div className="flex justify-center p-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <PageTitle
        title="Business Information"
        description="Update contact details and business settings"
        icon={<Building className="h-6 w-6" />}
        actions={
          <Button
            onClick={saveNow}
            disabled={!isDirty || isSaving}
            className="admin-btn-primary"
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            {isSaving ? 'Saving...' : 'Save Now'}
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <Label htmlFor="name" className="flex items-center space-x-2 text-gray-800 dark:text-gray-200 font-medium mb-1.5">
              <Building className="h-4 w-4 text-amber-600" />
              <span>Business Name</span>
            </Label>
            <Input
              id="name"
              value={businessData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Owaza Martial Arts"
              className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
            />
          </div>

          <div>
            <Label htmlFor="address" className="flex items-center space-x-2 text-gray-800 dark:text-gray-200 font-medium mb-1.5">
              <MapPin className="h-4 w-4 text-amber-600" />
              <span>Address</span>
            </Label>
            <Input
              id="address"
              value={businessData.address}
              onChange={(e) => handleChange('address', e.target.value)}
              placeholder="14726 County Line Rd, Spring Hill, FL 34610"
              className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
            />
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label htmlFor="phone" className="flex items-center space-x-2 text-gray-800 dark:text-gray-200 font-medium mb-1.5">
              <Phone className="h-4 w-4 text-amber-600" />
              <span>Office Phone</span>
            </Label>
            <Input
              id="phone"
              value={businessData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="(352) 428-0086"
              className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
            />
            <p className="text-xs text-stone-500 dark:text-stone-400 mt-1">Primary public contact — used for Call buttons, SEO, and email footers</p>
          </div>

          <div>
            <Label htmlFor="direct_line_phone" className="flex items-center space-x-2 text-gray-800 dark:text-gray-200 font-medium mb-1.5">
              <Phone className="h-4 w-4 text-amber-600" />
              <span>Direct Line (optional)</span>
            </Label>
            <Input
              id="direct_line_phone"
              value={businessData.direct_line_phone || ''}
              onChange={(e) => handleChange('direct_line_phone', e.target.value)}
              placeholder="(352) 428-0086"
              className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
            />
            <Input
              id="direct_line_label"
              value={businessData.direct_line_label || ''}
              onChange={(e) => handleChange('direct_line_label', e.target.value)}
              placeholder="Hanshi Jim Rivera's direct line"
              className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background mt-2"
            />
            <p className="text-xs text-stone-500 dark:text-stone-400 mt-1">A secondary/personal line (e.g. instructor's direct number). Label describes what it is.</p>
          </div>

          <div>
            <Label htmlFor="email" className="flex items-center space-x-2 text-gray-800 dark:text-gray-200 font-medium mb-1.5">
              <Mail className="h-4 w-4 text-amber-600" />
              <span>Email</span>
            </Label>
            <Input
              id="email"
              type="email"
              value={businessData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="info@owazamartialarts.com"
              className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
            />
          </div>

          <div>
            <Label htmlFor="quickstart_fee" className="flex items-center space-x-2 text-gray-800 dark:text-gray-200 font-medium mb-1.5">
              <span className="text-amber-600">$</span>
              <span>QuickStart Fee</span>
            </Label>
            <Input
              id="quickstart_fee"
              value={businessData.quickstart_fee}
              onChange={(e) => handleChange('quickstart_fee', e.target.value)}
              placeholder="$45"
              className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
            />
            <p className="text-xs text-stone-500 dark:text-stone-400 mt-1">Used in booking CTAs (e.g., "$45")</p>
          </div>
        </div>
      </div>

      <div className="flex justify-end items-center gap-3 pt-4 border-t border-stone-200">
        {isSaving && (
          <span className="text-sm text-amber-700 flex items-center">
            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            Saving...
          </span>
        )}
        {isDirty && !isSaving && (
          <Button
            onClick={saveNow}
            variant="adminGold"
          >
            <Save className="h-4 w-4 mr-2" />
            Save Now
          </Button>
        )}
      </div>
    </div>
  );
};

export default BusinessInfoEditor;